import * as React from "react";
import { ApprenantApi, ELevelName, AriReassignmentDto } from "../../../../api";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import * as Yup from "yup";
import styled from "styled-components";
import { Classes, Dialog } from "@blueprintjs/core";
import { CustomBulletList, FGWalterSelectInput, SmallFormGenerator } from "../../../../components";
import { ETLCodes } from "../../../../locales";
import { FGCustomPanel, FieldGroup } from "nsitools-react";
import { useReferential } from "../../../../hooks/useReferential";

const StyledDialog = styled(Dialog)`
  width: 700px;
  height: auto;
  background-color: white;
`;

export interface IAriReassignmentDialogProps {
  idsApprenant: number[];
  dialogOpen: boolean;
  onClose: () => void;
}

export const AriReassignmentDialog: React.FunctionComponent<IAriReassignmentDialogProps> = ({
  idsApprenant = [],
  dialogOpen,
  onClose
}) => {
  const { t } = useTl();
  const api = useApiService(ApprenantApi);

  const { data, loading, saveItem, saving, validationErrors } = useCrudApi(
    React.useMemo(
      () => ({
        getApiFn: () => {
          return api.apprenantGetAriReassignment({ request_body: idsApprenant });
        },
        saveApiFn: async (d: AriReassignmentDto) => {
          return api.apprenantSaveAriReassignment({ AriReassignmentDto: d });
        },
        onSaved: () => onClose()
      }),
      [api, idsApprenant, onClose]
    )
  );

  const FormSchema = React.useMemo(
    () =>
      Yup.object().shape({
        newIdAri: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required))
      }),
    [t]
  );

  const [usersAri, usersAriLoading] = useReferential(a =>
    a.referentialGetUsersByRoles({ ELevelName: [ELevelName.ARI], currentIduser: 0 })
  );

  return (
    <StyledDialog title={t(ETLCodes.ReassignationAri)} isOpen={dialogOpen} onClose={() => onClose()}>
      <div className={Classes.DIALOG_BODY}>
        <SmallFormGenerator
          initialValues={data}
          onSubmit={saveItem}
          editMode={true}
          validationSchema={FormSchema}
          onCancel={() => onClose()}
          showDeleteButton={false}
          saving={saving}
          loading={loading}
          validationErrors={validationErrors}
        >
          <FieldGroup>
            <FGCustomPanel>
              {ctx => (
                <CustomBulletList
                  title={t(ETLCodes.ARIsARemplacer)}
                  loading={loading}
                  items={
                    ctx?.formik?.values?.aris?.length > 0
                      ? ctx?.formik?.values?.aris?.map(c => ({ text: c }))
                      : [{ text: t(ETLCodes.Aucun) }]
                  }
                />
              )}
            </FGCustomPanel>
            <FGWalterSelectInput
              name="newIdAri"
              label={t(ETLCodes.AriRemplacant)}
              items={usersAri}
              loading={usersAriLoading}
            />
          </FieldGroup>
        </SmallFormGenerator>
      </div>
    </StyledDialog>
  );
};
